import { useEffect, useState } from "react";
import {
    DollarSign, Coins, Wallet, PiggyBank, Loader2, Play, RefreshCcw,
    ArrowRightLeft, ShieldCheck, AlertTriangle, ChevronRight, Waves,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../../components/ui/tabs";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../../components/ui/dialog";

const fmt = (v, dec = 2) => {
    if (v === null || v === undefined) return "—";
    const n = typeof v === "number" ? v : parseFloat(v);
    if (Number.isNaN(n)) return "—";
    return n.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
const fmtDate = (v) => v ? v.slice(0, 10) : "—";

export default function FinancieroTab({ facility }) {
    const [subtab, setSubtab] = useState("intereses");
    return (
        <div className="space-y-5" data-testid="tab-financiero-content">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h3 className="font-display text-xl flex items-center gap-2">
                        <Coins className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                        Motor financiero · A3
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Devengo diario de intereses, fees, reservas y ejecución determinista del waterfall.
                        Todos los cálculos en Decimal · rounding half-even (banker's rounding).
                    </p>
                </div>
            </div>

            <Tabs value={subtab} onValueChange={setSubtab}>
                <TabsList>
                    <TabsTrigger value="intereses" data-testid="fin-tab-intereses">Intereses</TabsTrigger>
                    <TabsTrigger value="fees" data-testid="fin-tab-fees">Fees</TabsTrigger>
                    <TabsTrigger value="reservas" data-testid="fin-tab-reservas">Reservas</TabsTrigger>
                    <TabsTrigger value="waterfall" data-testid="fin-tab-waterfall">Waterfall</TabsTrigger>
                </TabsList>
                <TabsContent value="intereses" className="mt-5">
                    <SubtabIntereses facility={facility} />
                </TabsContent>
                <TabsContent value="fees" className="mt-5">
                    <SubtabFees facility={facility} />
                </TabsContent>
                <TabsContent value="reservas" className="mt-5">
                    <SubtabReservas facility={facility} />
                </TabsContent>
                <TabsContent value="waterfall" className="mt-5">
                    <SubtabWaterfall facility={facility} />
                </TabsContent>
            </Tabs>
        </div>
    );
}


function SubtabIntereses({ facility }) {
    const { hasRole } = useAuth();
    const canWrite = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.TESORERIA, ROLES.FINANZAS, ROLES.ADMIN_TI]);
    const [rows, setRows] = useState([]);
    const [mtd, setMtd] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openDev, setOpenDev] = useState(false);
    const [desde, setDesde] = useState("");
    const [hasta, setHasta] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: r }, { data: m }] = await Promise.all([
                api.get(`/facilities/${facility.id}/interest-accruals`),
                api.get(`/facilities/${facility.id}/interes-mes-actual`),
            ]);
            setRows(r);
            setMtd(m);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al cargar");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    const devengar = async () => {
        if (!desde || !hasta) return toast.error("Rango requerido");
        try {
            const { data } = await api.post(`/facilities/${facility.id}/devengar-intereses`, { desde, hasta });
            toast.success(`Creados ${data.creados} · Skipped ${data.saltados} · Total $${fmt(data.total_interes_devengado)}`);
            setOpenDev(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KPI label="Interés MTD" value={`$${fmt(mtd?.total_devengado)}`} testid="interes-mtd-widget" />
                <KPI label="Días MTD" value={mtd?.total_dias ?? 0} />
                <KPI label="Pagado MTD" value={`$${fmt(mtd?.total_pagado)}`} />
                <KPI label="Pendiente MTD" value={`$${fmt(mtd?.total_no_pagado)}`} coral={parseFloat(mtd?.total_no_pagado || 0) > 0} />
            </div>

            {canWrite && (
                <Dialog open={openDev} onOpenChange={setOpenDev}>
                    <DialogTrigger asChild>
                        <Button size="sm" data-testid="devengar-intereses-btn">
                            <Play className="h-3 w-3 mr-1" /> Devengar rango
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Devengar intereses en rango</DialogTitle>
                            <DialogDescription>
                                Ejecuta el motor día por día. Idempotente por `(facility, fecha)`.
                                Aplica floor {facility.floor_pct || 3.5}% sobre {facility.tasa_base} · convención {facility.day_count_convention || "ACT_360"}.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Desde</Label>
                                <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="devengar-desde" />
                            </div>
                            <div>
                                <Label>Hasta</Label>
                                <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="devengar-hasta" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={devengar} data-testid="devengar-submit">Ejecutar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            <div className="rounded-md border border-border bg-card overflow-hidden">
                <table className="trf-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Saldo insoluto</th>
                            <th>Tasa base</th>
                            <th>Spread</th>
                            <th>All-in</th>
                            <th>Floor?</th>
                            <th>Convención</th>
                            <th>Interés día</th>
                            <th>Pagado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`accrual-row-${r.fecha_devengo}`}>
                                <td className="tabular">{r.fecha_devengo}</td>
                                <td className="tabular text-right">${fmt(r.saldo_insoluto)}</td>
                                <td className="tabular text-right">{fmt(r.tasa_base_pct, 4)}%</td>
                                <td className="tabular text-right">{fmt(r.spread_pct, 4)}%</td>
                                <td className="tabular text-right font-medium">{fmt(r.all_in_pct, 4)}%</td>
                                <td>{r.floor_aplicado ? <span className="badge-gold text-[10px]">FLOOR</span> : "—"}</td>
                                <td className="text-xs">{r.day_count_convention}</td>
                                <td className="tabular text-right">${fmt(r.interes_devengado_dia)}</td>
                                <td>{r.pagado ? <ShieldCheck className="h-3 w-3 text-emerald-600" /> : <span className="text-muted-foreground">—</span>}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && !loading && (
                            <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Sin devengos. Usa "Devengar rango" para arrancar.</td></tr>
                        )}
                        {loading && <tr><td colSpan={9} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function SubtabFees({ facility }) {
    const { hasRole } = useAuth();
    const canWrite = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.TESORERIA, ROLES.FINANZAS, ROLES.ADMIN_TI]);
    const [rows, setRows] = useState([]);
    const [periodo, setPeriodo] = useState("");
    const [openDev, setOpenDev] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/facilities/${facility.id}/fees`);
            setRows(data);
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    const devengar = async () => {
        if (!periodo) return toast.error("Periodo YYYY-MM requerido");
        try {
            const { data } = await api.post(`/facilities/${facility.id}/devengar-fees`, {
                periodo, tasa_anual_pct: 1.0,
            });
            if (data.skipped) toast.info(`Ya existe: ${periodo}`);
            else toast.success(`Fee $${fmt(data.monto)}`);
            setOpenDev(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                    Fees automáticos: collateral management (mensual) · origination · missed drawdown · renewal.
                </p>
                {canWrite && (
                    <Dialog open={openDev} onOpenChange={setOpenDev}>
                        <DialogTrigger asChild>
                            <Button size="sm" data-testid="devengar-fee-btn">
                                <Play className="h-3 w-3 mr-1" /> Devengar collateral mgmt
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Devengar collateral management fee</DialogTitle>
                                <DialogDescription>
                                    Base: monto_comprometido del facility × 1% p.a. / 12.
                                    Idempotente por periodo.
                                </DialogDescription>
                            </DialogHeader>
                            <div>
                                <Label>Periodo (YYYY-MM)</Label>
                                <Input value={periodo} onChange={(e) => setPeriodo(e.target.value)} placeholder="2026-09" data-testid="fee-periodo" />
                            </div>
                            <DialogFooter>
                                <Button onClick={devengar} data-testid="fee-submit">Ejecutar</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="rounded-md border border-border bg-card overflow-hidden">
                <table className="trf-table" data-testid="fees-table">
                    <thead>
                        <tr>
                            <th>Tipo</th>
                            <th>Concepto</th>
                            <th>Periodo</th>
                            <th>Base</th>
                            <th>Tasa</th>
                            <th>Monto</th>
                            <th>Estado</th>
                            <th>Devengo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((f) => (
                            <tr key={f.id} data-testid={`fee-row-${f.tipo}-${f.periodo}`}>
                                <td><span className="badge-gold text-[10px]">{f.tipo}</span></td>
                                <td className="text-xs">{f.concepto}</td>
                                <td className="tabular">{f.periodo}</td>
                                <td className="tabular text-right text-xs">${fmt(f.base_valor, 0)}</td>
                                <td className="tabular text-right">{fmt(f.tasa_pct, 2)}%</td>
                                <td className="tabular text-right font-medium">${fmt(f.monto)}</td>
                                <td>
                                    <span className={f.estado === "pagado" ? "badge-exito" : "badge-mock"}>
                                        {f.estado}
                                    </span>
                                </td>
                                <td className="tabular text-xs">{f.fecha_devengo}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Sin fees devengados.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function SubtabReservas({ facility }) {
    const { hasRole } = useAuth();
    const canWrite = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.TESORERIA, ROLES.FINANZAS, ROLES.ADMIN_TI]);
    const [rows, setRows] = useState([]);

    const load = async () => {
        try {
            const { data } = await api.get(`/facilities/${facility.id}/reservas`);
            setRows(data);
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    const recalcular = async () => {
        try {
            const { data } = await api.post(`/facilities/${facility.id}/reservas/recalcular`, { target_meses: 3 });
            toast.success(`Reserva ${data.estado} · target $${fmt(data.target_monto)}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const ultima = rows[0];

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KPI label="Target reserva" value={ultima ? `$${fmt(ultima.target_monto)}` : "—"} testid="reserva-target-widget" />
                <KPI label="Actual" value={ultima ? `$${fmt(ultima.monto_actual_en_cuenta_reserva)}` : "—"} />
                <KPI label="Delta" value={ultima ? `$${fmt(ultima.deficit_o_superavit)}` : "—"} coral={ultima?.estado === "deficit"} />
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Estado</div>
                    <div className={`font-display text-lg mt-1 ${ultima?.estado === "deficit" ? "text-[color:var(--brand-coral)]" : "text-emerald-600"}`} data-testid="reserva-deficit-badge">
                        {ultima?.estado || "sin datos"}
                    </div>
                </div>
            </div>

            {canWrite && (
                <Button size="sm" onClick={recalcular} data-testid="recalcular-reserva-btn">
                    <RefreshCcw className="h-3 w-3 mr-1" /> Recalcular reserva
                </Button>
            )}

            <div className="rounded-md border border-border bg-card overflow-hidden">
                <table className="trf-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Tipo</th>
                            <th>Target ({ultima?.target_meses || 3}m)</th>
                            <th>Actual</th>
                            <th>Delta</th>
                            <th>Estado</th>
                            <th>Top-up?</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td className="tabular">{r.fecha_calculo}</td>
                                <td><span className="badge-mock text-[10px]">{r.tipo}</span></td>
                                <td className="tabular text-right">${fmt(r.target_monto)}</td>
                                <td className="tabular text-right">${fmt(r.monto_actual_en_cuenta_reserva)}</td>
                                <td className={`tabular text-right ${r.estado === "deficit" ? "text-[color:var(--brand-coral)]" : "text-emerald-600"}`}>
                                    {parseFloat(r.deficit_o_superavit) >= 0 ? "+" : ""}${fmt(r.deficit_o_superavit)}
                                </td>
                                <td>{r.estado === "deficit" ?
                                    <span className="badge-error text-[10px]">DEFICIT</span> :
                                    <span className="badge-exito text-[10px]">OK</span>}</td>
                                <td>{r.requiere_top_up ? "Sí" : "—"}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Sin cálculos de reserva.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function SubtabWaterfall({ facility }) {
    const { hasRole } = useAuth();
    const canSim = true;  // todos pueden simular
    const canPropose = hasRole([ROLES.TESORERIA, ROLES.ADMIN_TI]);
    const canApproveT = hasRole([ROLES.TESORERIA, ROLES.ADMIN_TI]);
    const canApproveD = hasRole([ROLES.DIRECCION, ROLES.ADMIN_TI]);
    const [rows, setRows] = useState([]);
    const [monto, setMonto] = useState("500000");
    const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
    const [simulacion, setSimulacion] = useState(null);
    const [openSim, setOpenSim] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/facilities/${facility.id}/waterfall-ejecuciones`);
            setRows(data);
        } catch (err) {
            // fallback: usar filtro por facility_id contra endpoint viejo si existe
            try {
                const { data } = await api.get(`/facilities/${facility.id}/waterfall-ejecuciones`);
                setRows(data || []);
            } catch { setRows([]); }
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    const simular = async () => {
        if (!monto || !fecha) return toast.error("Monto y fecha requeridos");
        try {
            const { data } = await api.post(`/facilities/${facility.id}/waterfall/simular`, {
                monto: Number(monto), fecha,
            });
            setSimulacion(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const proponer = async () => {
        try {
            const { data } = await api.post(`/facilities/${facility.id}/waterfall/proponer`, {
                monto: Number(monto), fecha,
            });
            toast.success(`Propuesta creada ${data.id.slice(0, 8)}...`);
            setOpenSim(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex gap-3 items-end flex-wrap">
                <div>
                    <Label>Monto disponible (USD)</Label>
                    <Input type="number" value={monto} onChange={(e) => setMonto(e.target.value)} className="w-40" data-testid="waterfall-monto-input" />
                </div>
                <div>
                    <Label>Fecha</Label>
                    <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-40" data-testid="waterfall-fecha-input" />
                </div>
                <Button size="sm" onClick={simular} data-testid="simular-waterfall-btn">
                    <Waves className="h-3 w-3 mr-1" /> Simular
                </Button>
                {canPropose && simulacion && (
                    <Button size="sm" variant="outline" onClick={proponer} data-testid="proponer-waterfall-btn">
                        Proponer propuesta
                    </Button>
                )}
            </div>

            {simulacion && (
                <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="simulacion-panel">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-medium">Simulación</div>
                        {simulacion.bb_deficiency_al_ejecutar && (
                            <span className="badge-error text-[10px]"><AlertTriangle className="h-3 w-3" /> BB DEFICIENCY</span>
                        )}
                        {simulacion.covenant_breach_al_ejecutar && (
                            <span className="badge-error text-[10px]"><AlertTriangle className="h-3 w-3" /> COVENANT BREACH ACTIVO</span>
                        )}
                    </div>
                    <table className="trf-table">
                        <thead>
                            <tr>
                                <th>Orden</th>
                                <th>Concepto</th>
                                <th>Target</th>
                                <th>Asignado</th>
                                <th>Remanente</th>
                                <th>Suspendida</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(simulacion.lineas_ejecutadas || []).map((l) => (
                                <tr key={l.orden} data-testid={l.suspendida ? `linea-suspendida-${l.orden}` : `linea-${l.orden}`}>
                                    <td className="tabular">{l.orden}</td>
                                    <td className="text-xs">{l.concepto}</td>
                                    <td className="tabular text-right">${fmt(l.target_monto)}</td>
                                    <td className="tabular text-right font-medium">${fmt(l.monto_asignado)}</td>
                                    <td className="tabular text-right text-xs text-muted-foreground">${fmt(l.remanente_post_linea)}</td>
                                    <td>{l.suspendida ? <span className="badge-error text-[10px]" title={l.motivo_suspension}>SUSPENDIDA</span> : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="rounded-md border border-border bg-card overflow-hidden">
                <table className="trf-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Fecha</th>
                            <th>Monto</th>
                            <th>Total asignado</th>
                            <th>Estado</th>
                            <th>Aprobaciones</th>
                            <th>Instrucciones</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(rows || []).map((r) => (
                            <WaterfallRow
                                key={r.id} row={r}
                                canApproveT={canApproveT} canApproveD={canApproveD}
                                onChange={load}
                            />
                        ))}
                        {(!rows || rows.length === 0) && (
                            <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">
                                Sin ejecuciones. Simula y "Propón" (Tesorería) para arrancar el flujo maker-checker.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function WaterfallRow({ row, canApproveT, canApproveD, onChange }) {
    const yaAproboT = (row.aprobaciones || []).some((a) => a.rol === "tesoreria");
    const yaAproboD = (row.aprobaciones || []).some((a) => a.rol === "direccion");
    const aprobar = async (rol) => {
        try {
            const { data } = await api.post(`/waterfall-ejecuciones/${row.id}/aprobar`, {
                rol_aprobador: rol,
            });
            toast.success(`Aprobado como ${rol} · estado: ${data.estado}`);
            onChange();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };
    const ejecutar = async () => {
        try {
            const { data } = await api.post(`/waterfall-ejecuciones/${row.id}/ejecutar-final`);
            toast.success(`Ejecutado · ${data.instrucciones_creadas} instrucciones`);
            onChange();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };
    return (
        <tr data-testid={`waterfall-row-${row.id}`}>
            <td className="hash-cell text-xs">{row.id.slice(0, 8)}…</td>
            <td className="tabular text-xs">{fmtDate(row.fecha_propuesta)}</td>
            <td className="tabular text-right">${fmt(row.monto_input)}</td>
            <td className="tabular text-right">${fmt(row.total_asignado)}</td>
            <td>
                <span className={
                    row.estado === "ejecutada" ? "badge-exito" :
                        row.estado === "aprobada" ? "badge-gold" :
                            row.estado === "aprobada_parcial" ? "badge-mock" : "badge-mock"
                }>{row.estado}</span>
            </td>
            <td className="text-xs">
                {yaAproboT ? "✓T" : "—T"} · {yaAproboD ? "✓D" : "—D"}
            </td>
            <td className="tabular text-xs">{(row.instrucciones_ids || []).length}</td>
            <td>
                <div className="flex gap-1">
                    {row.estado === "propuesto" || row.estado === "aprobada_parcial" ? (
                        <>
                            {canApproveT && !yaAproboT && (
                                <Button size="sm" variant="outline" onClick={() => aprobar("tesoreria")} data-testid={`aprobar-waterfall-btn-tesoreria-${row.id}`}>
                                    Aprobar T
                                </Button>
                            )}
                            {canApproveD && !yaAproboD && (
                                <Button size="sm" variant="outline" onClick={() => aprobar("direccion")} data-testid={`aprobar-waterfall-btn-direccion-${row.id}`}>
                                    Aprobar D
                                </Button>
                            )}
                        </>
                    ) : null}
                    {row.estado === "aprobada" && canApproveT && (
                        <Button size="sm" onClick={ejecutar} data-testid={`ejecutar-waterfall-${row.id}`}>
                            Ejecutar
                        </Button>
                    )}
                </div>
            </td>
        </tr>
    );
}


function KPI({ label, value, testid, coral }) {
    return (
        <div className="kpi-card" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`font-display text-xl mt-1 ${coral ? "text-[color:var(--brand-coral)]" : ""}`}>{value}</div>
        </div>
    );
}
